import { File } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../lib/utils';

export interface DocumentTypeIconProps {
  /** Default 16px to match the events-grid document-type column icon slot. */
  size?: number;
  className?: string;
  /** Optional aria-label. When omitted, the glyph is decorative (`aria-hidden`). */
  'aria-label'?: string;
}

/**
 * DocumentTypeIcon — single lucide `File` glyph at the default 16px size.
 *
 * Per design §5.2 #25 (LOCKED 2026-05-20): this is intentionally a single-glyph
 * component, NOT a literal-union-keyed registry like `FileTypeIcon`. The Figma
 * source frame `5:5798` shows one document-type glyph across all 19 visible
 * rows; codifying multiple variants would violate
 * `feedback_no_demo_flags_in_registries.md` (production catalogs reflect actual
 * support, not demo display variation).
 *
 * If a future Figma frame introduces additional document-type glyph variants,
 * promote this component to a literal-union registry mirroring `FileTypeIcon`.
 */
export const DocumentTypeIcon: React.FC<DocumentTypeIconProps> = ({
  size = 16,
  className,
  'aria-label': ariaLabel,
}) => {
  return (
    <File
      width={size}
      height={size}
      className={cn(className)}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
};

DocumentTypeIcon.displayName = 'DocumentTypeIcon';
